import {Component, OnInit, Optional, ChangeDetectionStrategy} from '@angular/core';
import {PartnerTokenService} from '../../../../services/api-partner-token/partner-token.service';
import {ApiPartnerToken} from '../../../../models/api-partner-token';
import {LoaderService} from '../../../../services/loader/loader.service';

import {ConfirmDirective} from '../../../../directives/confirm/confirm.directive';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {ClipboardService} from '../../../../services/clipboard/clipboard.service';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {PartnerToolsTokenFormComponent} from './partner-tools-token-form/partner-tools-token-form.component';
import {MetaTitle} from '../../../../decorator/meta-title';
import {FormWidget} from '../../../../models/tool/form-widget';
import {formWidgets} from '../../../../constants/form-widgets';
import {forkJoin} from 'rxjs';
import {SiteService} from '../../../../services/site/site.service';
import {Site} from '../../../../models/site/site';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {
  endPointJs,
  folderSetJs,
  formWidgetJs,
  gridFormatJs, sourceDetailJs, sourceTypeJs,
  toolFormatIdJs
} from '../../../../constants/form-widget-js';
import {SafePipe} from '../../../../pipes/safe/safe.pipe';
import {PartnerTabComponent} from '../partner-tab/partner-tab.component';
import {Partner} from '../../../../models/partner/partner';
import {PartnerService} from '../../../../services/partner/partner.service';
import {ZodiacSign} from '../../../../constants/zodiac';

@Component({
  selector: 'app-partner-tools',
  imports: [
    ConfirmDirective,
    ReactiveFormsModule,
    FormsModule,
    SafePipe
],
  templateUrl: './partner-tools.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './partner-tools.component.scss'
})
@MetaTitle('Tools')
export class PartnerToolsComponent implements OnInit {
  partner: Partner | null = null;
  sites: Site[] = [];
  formWidgets: FormWidget[] = formWidgets;
  partnerTokens: ApiPartnerToken[] = [];
  selectedFormWidget: FormWidget | null = null;
  selectedUrl: string | null = null;

  constructor(
    protected copyToClipboard: ClipboardService,
    protected partnerService: PartnerService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private clipboard: ClipboardService,
    private dialog: DialogService,
    private partnerTokenService: PartnerTokenService,
    private siteService: SiteService,
    @Optional() private partnerTab: PartnerTabComponent
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();

    this.partnerTab.partnerSubject.subscribe((partner: Partner | null) => {
      this.partner = partner;
      if (this.partner) {
        this.fetchAll();
      }
    });
  }

  getZodiacSigns(): string[] {
    return Object.values(ZodiacSign);
  }

  fetchAll(): void {
    this.loader.startLoading();

    forkJoin([
      this.partnerTokenService.findAll({partnerId: this.partner?.id}),
      this.siteService.findAll({sort: 'name'}),
    ]).subscribe(([partnerTokens, sites]) => {
      this.partnerTokens = partnerTokens;
      this.sites = sites;

      this.loader.stopLoading();
    });
  }

  regenerate(partnerToken: ApiPartnerToken): void {
    this.loader.startLoading();
    this.partnerTokenService.regenerate(partnerToken).subscribe(() => {
      this.fetchAll();
      this.snackbar.success('API Token has been regenerated');
    });
  }

  delete(partnerToken: ApiPartnerToken): void {
    if (partnerToken.id) {
      this.loader.startLoading();
      this.partnerTokenService.delete(partnerToken.id).subscribe(() => {
        this.fetchAll();
        this.snackbar.success('API Token has been deleted');
      });
    }
  }

  copyToken(partnerToken: ApiPartnerToken, event: Event) {
    const target: any = event.target;

    if (partnerToken.token) {
      target.innerText = 'Copied!';
      this.clipboard.copy(partnerToken.token);
      setTimeout(() => {
        target.innerText = 'Copy';
      }, 2000);
    }
  }

  addToken(): void {
    this.dialog.openDialog(PartnerToolsTokenFormComponent, {
      data: {
        partner: this.partner
      }
    }).subscribe((closeParams) => {
      if (closeParams) {
        this.fetchAll();
      }
    });
  }

  editToken(partnerToken: ApiPartnerToken): void {
    this.dialog.openDialog(PartnerToolsTokenFormComponent, {
      data: {
        partner: this.partner,
        partnerToken
      }
    }).subscribe((closeParams) => {
      if (closeParams) {
        this.fetchAll();
      }
    });
  }

  getContainerCode(escape: boolean = false): string {
    let container = '<div class="embedded-form"></div>';

    if (escape) {
      container = container.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    return container;
  }

  getWidgetCode(escape: boolean = false) {
    if (!this.selectedUrl) {
      return 'Please select a widget and a site';
    }

    const url = this.selectedUrl + '/tools/form/';

    let code = this.selectedFormWidget ?
      formWidgetJs.replace('%endpoint%', url + this.selectedFormWidget.slug) :
      formWidgetJs.replace('%endpoint%', url + ' + tfi');

    const params: string[] = [];

    if (this.selectedFormWidget) {
      params.push(endPointJs.replace('%endpoint%', `'${url + this.selectedFormWidget.slug}?'`));

      if (this.selectedFormWidget.gridFormat) {
        params.push(gridFormatJs.replace('%gridFormat%', String(this.selectedFormWidget.gridFormat ?? '')));
      }

      if (this.selectedFormWidget.folderSet) {
        params.push(folderSetJs.replace('%folderSet%', String(this.selectedFormWidget.folderSet ?? '')));
      }

      if (this.selectedFormWidget.sourceType.trim()) {
        params.push(sourceTypeJs.replace('%sourceType%', String(this.selectedFormWidget.sourceType ?? '')));
      }

      if (this.selectedFormWidget.sourceDetails.length) {
        params.push(sourceDetailJs.replace('%sourceDetails%', JSON.stringify(this.selectedFormWidget.sourceDetails)));
      }
    } else {
      params.push(toolFormatIdJs);
      params.push(endPointJs.replace('%endpoint%', `'${url}' + tfi + '?'`));
      params.push(gridFormatJs.replace('%gridFormat%', '4'));
      params.push(folderSetJs.replace('%folderSet%', '1'));
    }

    code = code.replace('/* params */', params.join("\n"));

    if (escape) {
      code = code.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    return code;
  }

  generateRange(max: number): number[] {
    return Array.from({length: max}, (_, i) => i + 1);
  }
}
